import axios from "axios";

/**
 * Promotion API
 *
 * This file is intentionally frontend-safe:
 * - no aws-sdk
 * - no direct S3 access
 * - only HTTP calls to your backend
 *
 * Replace the endpoint paths below with your real API Gateway routes.
 */

// Prefer env override, then fall back to your existing prod API base if you use one.
// Change this if your project already centralizes API base URLs elsewhere.
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "";

/**
 * Small helper so endpoint composition stays clean.
 */
const buildUrl = (path) => `${API_BASE_URL}${path}`;

/**
 * Normalize axios errors into readable messages.
 */
const extractErrorMessage = (error, fallbackMessage) => {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallbackMessage
  );
};

/**
 * GET /promotion-templates
 * Returns an array of saved promotion templates.
 */
export const listPromotionTemplates = async () => {
  try {
    const response = await axios.get(buildUrl("/promotion-templates"));
    return response?.data?.templates || response?.data || [];
  } catch (error) {
    throw new Error(
      extractErrorMessage(error, "Failed to load promotion templates.")
    );
  }
};

/**
 * POST /promotion-templates
 * Saves a promotion template.
 */
export const savePromotionTemplate = async (template) => {
  try {
    if (!template || typeof template !== "object") {
      throw new Error("Template payload is required.");
    }

    const response = await axios.post(
      buildUrl("/promotion-templates"),
      template
    );

    return response?.data || { success: true };
  } catch (error) {
    throw new Error(
      extractErrorMessage(error, "Failed to save promotion template.")
    );
  }
};

/**
 * DELETE /promotion-templates/:templateId
 * Deletes a promotion template by id.
 */
export const deletePromotionTemplate = async (templateId) => {
  try {
    if (!templateId) {
      throw new Error("Template ID is required.");
    }

    const response = await axios.delete(
      buildUrl(`/promotion-templates/${encodeURIComponent(templateId)}`)
    );

    return response?.data || { success: true };
  } catch (error) {
    throw new Error(
      extractErrorMessage(error, "Failed to delete promotion template.")
    );
  }
};

/**
 * POST /promotion-images/upload-url
 * Requests a presigned upload URL for an image.
 *
 * Expected backend response shape:
 * {
 *   uploadUrl: string,
 *   fileUrl: string,
 *   key: string
 * }
 */
export const getPromotionImageUploadUrl = async ({
  fileName,
  contentType,
}) => {
  try {
    if (!fileName || !contentType) {
      throw new Error("fileName and contentType are required.");
    }

    const response = await axios.post(
      buildUrl("/promotion-images/upload-url"),
      {
        fileName,
        contentType,
      }
    );

    const data = response?.data || {};

    if (!data.uploadUrl || !data.fileUrl || !data.key) {
      throw new Error("Invalid upload URL response for promotion image.");
    }

    return data;
  } catch (error) {
    throw new Error(
      extractErrorMessage(error, "Failed to get image upload URL.")
    );
  }
};

/**
 * POST /promotion-attachments/upload-url
 * Requests a presigned upload URL for an attachment.
 *
 * Expected backend response shape:
 * {
 *   uploadUrl: string,
 *   fileUrl: string,
 *   key: string
 * }
 */
export const getPromotionAttachmentUploadUrl = async ({
  fileName,
  contentType,
}) => {
  try {
    if (!fileName || !contentType) {
      throw new Error("fileName and contentType are required.");
    }

    const response = await axios.post(
      buildUrl("/promotion-attachments/upload-url"),
      {
        fileName,
        contentType,
      }
    );

    const data = response?.data || {};

    if (!data.uploadUrl || !data.fileUrl || !data.key) {
      throw new Error("Invalid upload URL response for promotion attachment.");
    }

    return data;
  } catch (error) {
    throw new Error(
      extractErrorMessage(error, "Failed to get attachment upload URL.")
    );
  }
};

/**
 * DELETE /promotion-images/:key
 * Deletes an uploaded promotion image by object key.
 *
 * If your backend prefers body-based delete instead of path param,
 * change this implementation accordingly.
 */
export const deletePromotionImage = async ({ key }) => {
  try {
    if (!key) {
      throw new Error("Image key is required.");
    }

    const response = await axios.delete(
      buildUrl(`/promotion-images/${encodeURIComponent(key)}`)
    );

    return response?.data || { success: true };
  } catch (error) {
    throw new Error(
      extractErrorMessage(error, "Failed to delete promotion image.")
    );
  }
};

/**
 * DELETE /promotion-attachments/:key
 * Deletes an uploaded promotion attachment by object key.
 */
export const deletePromotionAttachment = async ({ key }) => {
  try {
    if (!key) {
      throw new Error("Attachment key is required.");
    }

    const response = await axios.delete(
      buildUrl(`/promotion-attachments/${encodeURIComponent(key)}`)
    );

    return response?.data || { success: true };
  } catch (error) {
    throw new Error(
      extractErrorMessage(error, "Failed to delete promotion attachment.")
    );
  }
};